import "../party/partyShell.css";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useUserCredits } from "../hooks/useAuth";
import { useIsMobileApp } from "../hooks/useIsMobileApp";
import {
  useApplyCoupon,
  useApplyDiscountCode,
  usePaymentMethods,
  usePurchase,
  usePurchaseOptions,
} from "../hooks/usePayments";
import { PartyCoinBadge, PartyTopBar } from "../party/PartyChrome";
import type { PartyShellPalette } from "../theme/theme";
import type { PaymentMethod, PurchaseOption } from "../types/api";
import { formatPrice } from "../utils/helpers";
import { MixpanelEvents, trackEvent } from "../utils/mixpanel";

const PurchasePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const partyPal = (theme.palette as unknown as { party: PartyShellPalette }).party;
  /** Matches `Stack direction={{ xs: "column", lg: "row" }}` — checkout stacks below options. */
  const stackedCheckoutLayout = useMediaQuery(theme.breakpoints.down("lg"));
  const navigate = useNavigate();
  const { data: purchaseOptions, isLoading: loadingOptions } = usePurchaseOptions();
  const { data: paymentMethods, isLoading: loadingPaymentMethods } = usePaymentMethods();
  const { data: creditsData } = useUserCredits();
  const purchase = usePurchase();
  const applyCoupon = useApplyCoupon();
  const applyDiscount = useApplyDiscountCode();
  const isMobileApp = useIsMobileApp();
  const availableCredits = creditsData?.credit ?? 0;

  const partyFieldSx = useMemo(
    () => ({
      "& .MuiOutlinedInput-root": {
        borderRadius: "14px",
        bgcolor: "rgba(0,0,0,0.04)",
        color: partyPal.paperInk,
        "& fieldset": {
          borderColor: `color-mix(in oklab, ${partyPal.paperInk} 20%, transparent)`,
        },
        "&:hover fieldset": {
          borderColor: `color-mix(in oklab, ${partyPal.paperInk} 35%, transparent)`,
        },
        "&.Mui-focused fieldset": {
          borderColor: partyPal.hot,
          borderWidth: 2,
        },
      },
      "& .MuiInputLabel-root": {
        color: `color-mix(in oklab, ${partyPal.paperInk} 65%, transparent)`,
        "&.Mui-focused": { color: partyPal.hot },
      },
      "& .MuiOutlinedInput-input": { color: partyPal.paperInk },
    }),
    [partyPal]
  );

  const partyOutlinedBtnSx = useMemo(
    () =>
      ({
        borderRadius: 999,
        textTransform: "none" as const,
        fontWeight: 800,
        borderColor: `color-mix(in oklab, ${partyPal.paperInk} 22%, transparent)`,
        color: partyPal.paperInk,
        px: 2,
        "&:hover": {
          borderColor: partyPal.hot,
          bgcolor: `color-mix(in oklab, ${partyPal.hot} 10%, transparent)`,
        },
      }) as const,
    [partyPal]
  );

  const partyPrimaryCtaSx = useMemo(
    () => ({
      borderRadius: 999,
      py: 1.35,
      px: 2,
      textTransform: "none" as const,
      fontSize: 16,
      fontFamily: "var(--font-display), Rubik, Tajawal, sans-serif",
      fontWeight: 800,
      letterSpacing: "-0.01em",
      bgcolor: partyPal.hot,
      color: "#fff",
      boxShadow: `0 1px 0 rgba(255,255,255,0.3) inset, 0 8px 0 color-mix(in oklab, ${partyPal.hot} 45%, #000)`,
      "&:hover": {
        bgcolor: `color-mix(in oklab, ${partyPal.hot} 90%, #000)`,
      },
      "&:active": { transform: "translateY(2px) scale(0.99)" },
      "&.Mui-disabled": { opacity: 0.55, color: "#fff" },
    }),
    [partyPal]
  );

  const dialogPaperSx = useMemo(
    () => ({
      bgcolor: partyPal.bg2,
      border: `1.5px solid ${partyPal.inkFaint}`,
      borderRadius: 3,
      color: partyPal.ink,
      backgroundImage: "none",
    }),
    [partyPal]
  );

  const [selectedOption, setSelectedOption] = useState<PurchaseOption | null>(null);
  const [couponCode, setCouponCode] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [discountValue, setDiscountValue] = useState<number | null>(null);
  const [discountCodeId, setDiscountCodeId] = useState<number | null>(null);
  const [discountError, setDiscountError] = useState<string | null>(null);
  const [couponApplyError, setCouponApplyError] = useState<string | null>(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
  const [showCouponSuccessDialog, setShowCouponSuccessDialog] = useState(false);
  const [couponCredits, setCouponCredits] = useState<number | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<number | null>(null);

  const loading = loadingOptions;
  const error = purchase.error || applyCoupon.error || applyDiscount.error;

  useEffect(() => {
    trackEvent(MixpanelEvents.PAGE_VIEWED, {
      page_name: "Purchase Page",
      path: "/purchase",
    });
  }, []);

  const filteredPaymentMethods = useMemo(() => {
    const methods = paymentMethods ?? [];
    const platform = navigator.platform;
    const isAppleDevice =
      /iPad|iPhone|iPod|Mac/i.test(platform) ||
      (typeof navigator.userAgent === "string" &&
        /iPad|iPhone|iPod|Macintosh/i.test(navigator.userAgent));
    return methods.filter((pm) => {
      const name = `${pm.name || ""} ${pm.name_ar || ""}`.toLowerCase();
      const isApplePay = name.includes("apple");
      return !isApplePay || isAppleDevice;
    });
  }, [paymentMethods]);

  const getPaymentMethodLabel = (pm: PaymentMethod): string => {
    const fallback = rtl && pm.name_ar ? pm.name_ar : pm.name;
    const byId: Record<number, string> = {
      1: "purchase.paymentMethods.knet",
      2: "purchase.paymentMethods.creditCard",
      24: "purchase.paymentMethods.applePay",
    };
    const key = byId[pm.myfatoorah_id];
    if (!key) return fallback;
    return t(key, { defaultValue: fallback });
  };

  useEffect(() => {
    if (!filteredPaymentMethods.length) {
      setSelectedPaymentMethod(null);
      return;
    }
    setSelectedPaymentMethod((prev) => {
      if (prev != null && filteredPaymentMethods.some((m) => m.myfatoorah_id === prev)) return prev;
      return filteredPaymentMethods[0].myfatoorah_id;
    });
  }, [filteredPaymentMethods]);

  const handleApplyCoupon = () => {
    if (!couponCode.trim()) return;
    setCouponApplyError(null);
    applyCoupon.mutate(couponCode.trim(), {
      onSuccess: (result) => {
        trackEvent(MixpanelEvents.COUPON_APPLIED, {
          coupon_code: couponCode.trim(),
          credits_received: result.credits,
        });
        setCouponCredits(result.credits);
        setShowCouponSuccessDialog(true);
        setCouponCode("");
      },
      onError: (err: unknown) => {
        const errorMessage =
          (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || t("common.error");
        trackEvent(MixpanelEvents.COUPON_APPLY_FAILED, {
          coupon_code: couponCode.trim(),
          error_message: errorMessage,
          error_detail: (err as { response?: { data?: unknown } })?.response?.data,
        });
        setCouponApplyError(errorMessage);
      },
    });
  };

  const handleApplyDiscount = () => {
    if (!discountCode.trim() || !selectedOption) return;
    const optId = selectedOption.id;
    setDiscountError(null);
    applyDiscount.mutate(discountCode.trim(), {
      onSuccess: (result) => {
        trackEvent(MixpanelEvents.DISCOUNT_CODE_APPLIED, {
          discount_code: discountCode.trim(),
          discount_code_id: result.discount_code_id,
          discount_value: parseFloat(result.discount_value),
          purchase_option_id: optId,
        });
        setDiscountValue(parseFloat(result.discount_value));
        setDiscountCodeId(result.discount_code_id);
      },
      onError: (err: unknown) => {
        const errorMessage =
          (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || t("common.error");
        trackEvent(MixpanelEvents.DISCOUNT_CODE_APPLY_FAILED, {
          discount_code: discountCode.trim(),
          error_message: errorMessage,
          error_detail: (err as { response?: { data?: unknown } })?.response?.data,
          purchase_option_id: optId,
        });
        setDiscountError(errorMessage);
        setDiscountValue(null);
        setDiscountCodeId(null);
      },
    });
  };

  const handlePurchase = () => {
    if (!selectedOption) return;

    trackEvent(MixpanelEvents.PURCHASE_INITIATED, {
      purchase_option_id: selectedOption.id,
      purchase_option_name: selectedOption.name,
      price: selectedOption.final_price,
      credits: selectedOption.credits,
      payment_method: selectedPaymentMethod,
      has_discount: !!discountValue,
      discount_code: discountCode || null,
    });

    purchase.mutate(
      {
        purchase_option: selectedOption.id,
        discount_code: discountCodeId || undefined,
        payment_method: selectedPaymentMethod || undefined,
        is_app: isMobileApp,
      },
      {
        onSuccess: (result) => {
          trackEvent(MixpanelEvents.PURCHASE_COMPLETED, {
            purchase_option_id: selectedOption.id,
            purchase_option_name: selectedOption.name,
            price: selectedOption.final_price,
            credits: selectedOption.credits,
            has_discount: !!discountValue,
            discount_value: discountValue,
            payment_url: result.payment_url ? "provided" : null,
          });
          if (result.payment_url) {
            setPaymentUrl(result.payment_url);
            setShowPaymentDialog(true);
          } else if (result.invoice) {
            alert(
              `${t("purchase.paymentSuccess")} — ${result.invoice.credits} ${t("purchase.credits")}`
            );
            navigate("/");
          } else {
            alert(t("purchase.paymentSuccess"));
            navigate("/");
          }
        },
        onError: (err: unknown) => {
          const errorMessage =
            (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || t("common.error");
          trackEvent(MixpanelEvents.PURCHASE_FAILED, {
            purchase_option_id: selectedOption.id,
            purchase_option_name: selectedOption.name,
            error_message: errorMessage,
            error_detail: (err as { response?: { data?: unknown } })?.response?.data,
            has_discount: !!discountValue,
            discount_code_id: discountCodeId,
          });
          setDiscountError(errorMessage);
        },
      }
    );
  };

  const calculateTotal = (): string => {
    if (!selectedOption) return "0.000";
    let total = parseFloat(selectedOption.final_price);
    if (discountValue) {
      total = total * (1 - discountValue / 100);
    }
    return total.toFixed(3);
  };

  const rtl = i18n.language === "ar";

  const optionBorder = `1.5px solid color-mix(in oklab, ${partyPal.paperInk} 14%, transparent)`;

  return (
    <Box
      className="party-shell-root party-flow-wide"
      dir={rtl ? "rtl" : "ltr"}
      lang={i18n.language || "en"}
      data-party-theme="midnight"
      data-party-type="rubik"
      sx={{
        minHeight: "100dvh",
        width: "100%",
        bgcolor: partyPal.bg,
        color: partyPal.ink,
        pb: stackedCheckoutLayout
          ? `calc(96px + env(safe-area-inset-bottom, 0px))`
          : theme.spacing(6),
      }}
    >
      <Box className="party-flow-inner" sx={{ maxWidth: 1100, mx: "auto" }}>
        <PartyTopBar title={t("purchase.title")} onBack={() => navigate("/")} />

        <Stack spacing={2.5} sx={{ px: 2.5, pt: 0.5 }}>
          <Paper
            elevation={0}
            className="party-kan-card"
            style={{ "--card-bg": partyPal.paper } as React.CSSProperties}
            sx={{
              p: 2.5,
              color: partyPal.paperInk,
              border: `1.5px solid color-mix(in oklab, ${partyPal.paperInk} 12%, transparent)`,
            }}
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5} alignItems={{ sm: "center" }} justifyContent="space-between">
              <Stack direction="row" spacing={2} alignItems="center">
                <PartyCoinBadge amount={availableCredits} />
                <Box>
                  <Typography className="party-mono" sx={{ fontSize: 11, letterSpacing: "0.12em", color: `color-mix(in oklab, ${partyPal.paperInk} 55%, transparent)` }}>
                    {t("purchase.creditsLabel")}
                  </Typography>
                  <Typography className="party-display" sx={{ fontSize: "clamp(32px, 8vw, 44px)", fontWeight: 900, lineHeight: 1.1, color: partyPal.paperInk }}>
                    {availableCredits}
                  </Typography>
                </Box>
              </Stack>
              <Typography sx={{ fontSize: 14, lineHeight: 1.5, color: `color-mix(in oklab, ${partyPal.paperInk} 72%, transparent)`, maxWidth: 480 }}>
                {t("purchase.balanceHeroHint")}
              </Typography>
            </Stack>
          </Paper>

          {couponApplyError && (
            <Alert severity="error" onClose={() => setCouponApplyError(null)} sx={{ borderRadius: 2 }}>
              {couponApplyError}
            </Alert>
          )}

          {(error || discountError) && (
            <Alert
              severity={discountError ? "warning" : "error"}
              sx={{
                mb: 0,
                borderRadius: 2,
                bgcolor: `color-mix(in oklab, ${partyPal.ink} 6%, ${partyPal.bg2})`,
                color: partyPal.ink,
                border: `1px solid ${partyPal.inkFaint}`,
                "& .MuiAlert-icon": { color: partyPal.hot },
              }}
              onClose={() => {
                setDiscountError(null);
              }}
            >
              {discountError || (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail || t("common.error")}
            </Alert>
          )}

          <Stack direction={{ xs: "column", lg: "row" }} spacing={3} alignItems="flex-start">
            <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
              <Typography className="party-display" sx={{ fontSize: 22, mb: 2, color: partyPal.ink }}>
                {t("purchase.selectOption")}
              </Typography>

              {loading && (
                <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
                  <CircularProgress sx={{ color: partyPal.hot }} />
                </Box>
              )}

              {!loading && (!purchaseOptions || purchaseOptions.length === 0) && (
                <Typography sx={{ color: partyPal.inkDim, fontSize: 15 }}>{t("purchase.noOptions")}</Typography>
              )}

              {!loading && purchaseOptions && purchaseOptions.length > 0 && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(1, minmax(0, 1fr))",
                      sm: "repeat(2, minmax(0, 1fr))",
                      lg: "repeat(3, minmax(0, 1fr))",
                    },
                    gap: 2,
                  }}
                >
                  {purchaseOptions.map((opt) => {
                    const isSelected = selectedOption?.id === opt.id;
                    return (
                      <Paper
                        key={opt.id}
                        elevation={0}
                        className="party-kan-card"
                        onClick={() => {
                          trackEvent(MixpanelEvents.PURCHASE_OPTION_SELECTED, {
                            purchase_option_id: opt.id,
                            purchase_option_name: opt.name,
                            price: opt.final_price,
                            credits: opt.credits,
                          });
                          setSelectedOption(opt);
                          setDiscountValue(null);
                          setDiscountCodeId(null);
                          setDiscountCode("");
                          setDiscountError(null);
                        }}
                        style={{ "--card-bg": partyPal.paper } as React.CSSProperties}
                        sx={{
                          p: 2,
                          cursor: "pointer",
                          borderRadius: 3,
                          border: isSelected ? `1.5px solid ${partyPal.hot}` : optionBorder,
                          bgcolor: isSelected
                            ? `color-mix(in oklab, ${partyPal.hot} 12%, ${partyPal.paper})`
                            : partyPal.paper,
                          color: partyPal.paperInk,
                          boxShadow: isSelected ? `0 0 0 2px color-mix(in oklab, ${partyPal.hot} 55%, transparent)` : "none",
                          transition: "border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease",
                        }}
                      >
                        <Stack spacing={1.25} sx={{ height: "100%" }}>
                          <Typography sx={{ fontWeight: 800, fontSize: 17, color: partyPal.paperInk }}>{opt.name}</Typography>
                          <Typography sx={{ fontSize: 14, color: `color-mix(in oklab, ${partyPal.paperInk} 58%, transparent)` }}>
                            {opt.credits} {t("purchase.credits")}
                          </Typography>
                          <Typography className="party-display" sx={{ fontSize: 22, fontWeight: 900, color: partyPal.hot, mt: "auto" }}>
                            {formatPrice(opt.final_price)}
                          </Typography>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </Box>

            <Paper
              elevation={0}
              className="party-kan-card"
              style={{ "--card-bg": partyPal.paper } as React.CSSProperties}
              sx={{
                width: { xs: "100%", lg: 340 },
                flexShrink: 0,
                p: 2.5,
                color: partyPal.paperInk,
                border: `1.5px solid color-mix(in oklab, ${partyPal.paperInk} 12%, transparent)`,
              }}
            >
              <Typography className="party-display" sx={{ fontSize: 20, mb: 1.5, color: partyPal.paperInk }}>
                {t("purchase.checkout")}
              </Typography>

              <Typography className="party-mono" sx={{ fontSize: 11, letterSpacing: "0.1em", color: `color-mix(in oklab, ${partyPal.paperInk} 50%, transparent)`, mb: 0.75 }}>
                {t("purchase.paymentMethod", { defaultValue: "Payment method" })}
              </Typography>
              <Stack spacing={0.9} sx={{ mb: 2 }}>
                {loadingPaymentMethods ? (
                  <CircularProgress size={18} sx={{ color: partyPal.hot }} />
                ) : filteredPaymentMethods.length ? (
                  filteredPaymentMethods.map((pm: PaymentMethod) => {
                    const selected = selectedPaymentMethod === pm.myfatoorah_id;
                    const label = getPaymentMethodLabel(pm);
                    if (pm.myfatoorah_id === 24) {
                      return (
                        <Button
                          key={pm.id}
                          variant="contained"
                          onClick={() => {
                            if (selected) {
                              handlePurchase()
                            } else {

                              setSelectedPaymentMethod(pm.myfatoorah_id)
                            }
                          }}
                          sx={{
                            justifyContent: "center",
                            borderRadius: 2,
                            textTransform: "none",
                            border: selected
                              ? `2px solid ${partyPal.hot}`
                              : "2px solid transparent",
                            bgcolor: "#000",
                            color: "#fff",
                            px: 1.25,
                            py: 1.2,
                            fontWeight: 800,
                            boxShadow: selected
                              ? `0 0 0 2px color-mix(in oklab, ${partyPal.hot} 45%, transparent)`
                              : "none",
                            "&:hover": {
                              bgcolor: "#111",
                            },
                          }}
                        >
                          Pay
                        </Button>
                      );
                    }
                    return (
                      <Button
                        key={pm.id}
                        variant="outlined"
                        onClick={() => setSelectedPaymentMethod(pm.myfatoorah_id)}
                        sx={{
                          justifyContent: "space-between",
                          borderRadius: 2,
                          textTransform: "none",
                          borderColor: selected ? partyPal.hot : `color-mix(in oklab, ${partyPal.paperInk} 22%, transparent)`,
                          bgcolor: selected
                            ? `color-mix(in oklab, ${partyPal.hot} 12%, ${partyPal.paper})`
                            : "transparent",
                          color: partyPal.paperInk,
                          px: 1.25,
                          py: 0.9,
                          "&:hover": {
                            borderColor: partyPal.hot,
                            bgcolor: `color-mix(in oklab, ${partyPal.hot} 10%, ${partyPal.paper})`,
                          },
                        }}
                      >
                        <span>{label}</span>
                        <span style={{ opacity: selected ? 1 : 0.35 }}>●</span>
                      </Button>
                    );
                  })
                ) : (
                  <Typography sx={{ fontSize: 13, color: `color-mix(in oklab, ${partyPal.paperInk} 60%, transparent)` }}>
                    {t("purchase.noPaymentMethods", { defaultValue: "No payment methods available" })}
                  </Typography>
                )}
              </Stack>

              <Typography sx={{ fontSize: 14, color: `color-mix(in oklab, ${partyPal.paperInk} 62%, transparent)`, mb: 2 }}>
                {selectedOption ? `${selectedOption.credits} ${t("purchase.credits")}` : t("purchase.selectOption")}
              </Typography>

              <Typography className="party-mono" sx={{ fontSize: 11, letterSpacing: "0.1em", color: `color-mix(in oklab, ${partyPal.paperInk} 50%, transparent)`, mb: 0.5 }}>
                {t("purchase.total")}
              </Typography>
              <Typography className="party-display" sx={{ fontSize: 28, fontWeight: 900, color: partyPal.hot, mb: 2 }}>
                {selectedOption ? `${calculateTotal()} KWD` : `0.000 KWD`}
              </Typography>

              {selectedOption && discountValue != null && (
                <Typography sx={{ fontSize: 13, color: partyPal.violet, mb: 1.5, fontWeight: 700 }}>
                  {discountValue}% {t("purchase.applyDiscount")} — {t("purchase.applied")}
                </Typography>
              )}

              <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: "flex-start" }}>
                <TextField
                  size="small"
                  label={t("purchase.discountCode")}
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value)}
                  disabled={!selectedOption}
                  sx={{ flex: 1, ...partyFieldSx }}
                />
                <Button
                  variant="outlined"
                  onClick={handleApplyDiscount}
                  disabled={!selectedOption || applyDiscount.isPending || !discountCode.trim()}
                  sx={{ ...partyOutlinedBtnSx, flexShrink: 0, py: 1 }}
                >
                  {applyDiscount.isPending ? <CircularProgress size={20} sx={{ color: partyPal.hot }} /> : t("purchase.applyDiscount")}
                </Button>
              </Stack>

              <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: "flex-start" }}>
                <TextField
                  size="small"
                  label={t("purchase.couponCode")}
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                  sx={{ flex: 1, ...partyFieldSx }}
                />
                <Button
                  variant="outlined"
                  onClick={handleApplyCoupon}
                  disabled={applyCoupon.isPending || !couponCode.trim()}
                  sx={{ ...partyOutlinedBtnSx, flexShrink: 0, py: 1 }}
                >
                  {applyCoupon.isPending ? <CircularProgress size={20} sx={{ color: partyPal.hot }} /> : t("purchase.applyCoupon")}
                </Button>
              </Stack>

              <Button
                fullWidth
                variant="contained"
                disableElevation
                onClick={handlePurchase}
                disabled={!selectedOption || purchase.isPending || !selectedPaymentMethod}
                className="party-btn party-btn-hot"
                sx={{
                  ...partyPrimaryCtaSx,
                  mt: 0.5,
                  display: { xs: "none", lg: "block" },
                }}
              >
                {purchase.isPending ? <CircularProgress size={22} color="inherit" /> : t("purchase.purchase")}
              </Button>
            </Paper>
          </Stack>
        </Stack>
      </Box>

      {stackedCheckoutLayout ? (
        <Box
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: theme.zIndex.appBar,
            px: 2,
            pt: 1.5,
            pb: `calc(${theme.spacing(2)} + env(safe-area-inset-bottom, 0px))`,
            maxWidth: 1100,
            mx: "auto",
            bgcolor: `color-mix(in oklab, ${partyPal.bg} 92%, ${partyPal.paper})`,
            borderTop: `1.5px solid ${partyPal.inkFaint}`,
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            boxShadow: `0 -8px 24px color-mix(in oklab, ${partyPal.bg} 40%, transparent)`,
          }}
        >
          <Stack spacing={0.75}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1}>
              <Typography className="party-mono" sx={{ fontSize: 10, letterSpacing: "0.08em", color: partyPal.inkDim }}>
                {t("purchase.total")}
              </Typography>
              <Typography className="party-display" sx={{ fontSize: 20, fontWeight: 900, color: partyPal.hot }}>
                {selectedOption ? `${calculateTotal()} KWD` : `0.000 KWD`}
              </Typography>
            </Stack>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={handlePurchase}
              disabled={!selectedOption || purchase.isPending || !selectedPaymentMethod}
              className="party-btn party-btn-hot"
              sx={{ ...partyPrimaryCtaSx, mt: 0 }}
            >
              {purchase.isPending ? <CircularProgress size={22} color="inherit" /> : t("purchase.purchase")}
            </Button>
          </Stack>
        </Box>
      ) : null}

      <Dialog
        open={showPaymentDialog}
        onClose={() => setShowPaymentDialog(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: { sx: dialogPaperSx },
        }}
      >
        <DialogTitle sx={{ color: partyPal.ink, fontFamily: "var(--font-display)", fontWeight: 800 }}>
          {t("purchase.title")}
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: partyPal.inkFaint, color: partyPal.ink }}>
          {paymentUrl ? (
            <>
              <Typography variant="body2" sx={{ mb: 2, color: partyPal.inkDim }}>
                {t("purchase.redirectingToPayment")}
              </Typography>
              <Button variant="contained" fullWidth disableElevation onClick={() => (window.location.href = paymentUrl)} sx={{ mb: 1, ...partyPrimaryCtaSx }}>
                {t("purchase.proceedToPayment")}
              </Button>
              <Typography variant="caption" sx={{ color: partyPal.inkDim }}>
                {t("purchase.paymentNote")}
              </Typography>
            </>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setShowPaymentDialog(false)} sx={{ ...partyOutlinedBtnSx, borderColor: partyPal.inkFaint, color: partyPal.ink }}>
            {t("common.cancel")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showCouponSuccessDialog}
        onClose={() => {
          setShowCouponSuccessDialog(false);
          navigate("/");
        }}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: { sx: dialogPaperSx },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pb: 1, color: partyPal.ink, fontWeight: 800, fontFamily: "var(--font-display)" }}>
          {t("purchase.paymentSuccess")}
        </DialogTitle>
        <DialogContent dividers sx={{ textAlign: "center", py: 3, borderColor: partyPal.inkFaint, color: partyPal.ink }}>
          <Typography sx={{ mb: 2, fontWeight: 800, fontSize: 22 }}>{t("purchase.couponAppliedSuccess")}</Typography>
          {couponCredits !== null && (
            <Stack spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
              <Typography sx={{ color: partyPal.inkDim }}>{t("purchase.newCreditBalance")}</Typography>
              <Typography className="party-display" sx={{ fontWeight: 900, fontSize: 40, color: partyPal.gold }}>
                {couponCredits} {t("purchase.credits")}
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 2, px: 3 }}>
          <Button
            variant="contained"
            disableElevation
            onClick={() => {
              setShowCouponSuccessDialog(false);
              navigate("/");
            }}
            sx={{ minWidth: 140, ...partyPrimaryCtaSx }}
          >
            {t("common.continue")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurchasePage;
